/**
 * VenueCombobox — searchable venue dropdown with "+ Add" inline create.
 * Type to filter; if no match, "+ Add '{typed}' as new venue" appears at the
 * bottom of the list and creates the venue on click (calls venuesAPI.create).
 */
import React, { useState } from 'react';
import { Check, ChevronsUpDown, Plus, Loader2, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from './ui/command';
import { venuesAPI } from '../lib/api';
import { toast } from 'sonner';

export default function VenueCombobox({ venues = [], value, onChange, onVenueCreated }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);

  const selected = venues.find(v => v.venue_id === value);
  const normalizedSearch = (search || '').trim();
  const exactMatch = venues.find(v => (v.name || '').toLowerCase() === normalizedSearch.toLowerCase());
  const canCreate = normalizedSearch.length >= 2 && !exactMatch && !creating;

  const handleCreate = async () => {
    if (!canCreate) return;
    setCreating(true);
    try {
      const res = await venuesAPI.create({
        name: normalizedSearch,
        address: '',
        courts: 1,
        description: 'Added via tournament create flow',
        hourly_rate: 0,
        session_rate: 0,
      });
      const newVenue = res.data;
      onVenueCreated?.(newVenue);
      onChange(newVenue.venue_id);
      toast.success(`Added "${newVenue.name}"`);
      setOpen(false);
      setSearch('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add venue');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          data-testid="venue-combobox-trigger"
        >
          <span className="truncate flex items-center gap-2">
            {selected ? (
              <>
                <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                {selected.name}
              </>
            ) : (
              <span className="text-muted-foreground">Select venue or type to add new…</span>
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
        <Command shouldFilter={true}>
          <CommandInput
            placeholder="Search or type new venue name…"
            value={search}
            onValueChange={setSearch}
            data-testid="venue-combobox-input"
          />
          <CommandList>
            <CommandEmpty>
              {canCreate ? (
                <button
                  type="button"
                  onClick={handleCreate}
                  className="flex items-center justify-center gap-2 w-full py-3 text-sm font-semibold text-primary-foreground bg-primary hover:bg-primary/90 rounded-md transition-colors"
                  data-testid="venue-combobox-add-empty"
                >
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  Add "{normalizedSearch}" as new venue
                </button>
              ) : (
                <span className="block py-4 text-center text-sm text-muted-foreground">
                  Type at least 2 characters to add a new venue
                </span>
              )}
            </CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__none__"
                onSelect={() => { onChange(''); setOpen(false); setSearch(''); }}
                data-testid="venue-combobox-none"
              >
                <Check className={`mr-2 h-4 w-4 ${!value ? 'opacity-100' : 'opacity-0'}`} />
                <span className="text-muted-foreground">No venue</span>
              </CommandItem>
              {venues.map(v => (
                <CommandItem
                  key={v.venue_id}
                  value={v.name}
                  onSelect={() => { onChange(v.venue_id); setOpen(false); setSearch(''); }}
                  data-testid={`venue-combobox-item-${v.venue_id}`}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === v.venue_id ? 'opacity-100' : 'opacity-0'}`} />
                  <MapPin className="mr-1.5 h-3 w-3 text-muted-foreground" />
                  {v.name}
                  {v.address && <span className="ml-2 text-[10px] text-muted-foreground truncate">— {v.address}</span>}
                </CommandItem>
              ))}
              {canCreate && (
                <CommandItem
                  value={`add-${normalizedSearch}`}
                  onSelect={handleCreate}
                  className="border-t border-border mt-1 pt-2 text-primary font-semibold data-[selected=true]:bg-primary/10"
                  data-testid="venue-combobox-add"
                >
                  {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                  Add "{normalizedSearch}" as new venue
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
